import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { observer } from 'mobx-react-lite'
import ReactDatePicker from 'react-datepicker'
import { Link, useParams } from 'react-router'

// evertel
import { Card, Input, Col, Row, Modal, ModalBody, useUI, Checkbox, ModalHeader, Text, Button, Spinner } from '@evertel/web/ui'
import { Message } from '../../Message'
import { useService } from '@evertel/di'
import { MessageSearchController } from '@evertel/message'
import { Analytics } from '@evertel/analytics'
import { FetchUserService } from '@evertel/blue-user'

interface MessageSearchProps {
    visible: boolean
    modelType: 'room' | 'thread'
    modelId: number // roomId or threadId
    modelName: string // room/thread name
    onClose?: () => void
}

const MessageSearchModal: React.FC<MessageSearchProps> = observer(({
    modelType = 'room',
    modelId,
    modelName,
    visible,
    onClose
}) => {
    const messageSearchController = useService(MessageSearchController, [modelId])
    const fetchUserService = useService(FetchUserService, [])
    const analytics = useService(Analytics, [])

    const [dateRange, setDateRange] = useState<[Date|null, Date|null]>([null, null])
    const [searchTerm, setSearchTerm] = useState('')

    const searchInputRef = useRef<HTMLInputElement>(null)

    const { addToast } = useUI()

    const { repliesToId: currentRepliesToIdString } = useParams<{ repliesToId?: string }>()
    const currentRepliesToId = currentRepliesToIdString ? parseInt(currentRepliesToIdString, 10) : null

    const isRoom = (modelType === 'room')

    useEffect(() => {
        if (visible) {
            // Only fully reset when the room/thread context changes
            const contextChanged = messageSearchController.modelId !== modelId || messageSearchController.searchScope !== modelType
            if (contextChanged) {
                setDateRange([null, null])
                setSearchTerm('')
                messageSearchController.init(modelId, modelType) // init calls reset()
            } else {
                // Same context — sync local state from controller and re-trigger search
                setSearchTerm(messageSearchController.searchTerm)
                setDateRange([messageSearchController.startDate, messageSearchController.endDate])
                if (messageSearchController.searchTerm) {
                    messageSearchController.debouncedSearch()
                }
            }

            // Focus the search input after modal transition completes
            setTimeout(() => {
                searchInputRef.current?.focus()
            }, 200) // slightly longer than modal's 150ms duration

            if (isRoom) {
                analytics.logView('/rooms/search', true)
            } else {
                analytics.logView('/threads/search', true)
            }
        }
    }, [visible, modelId, modelType, messageSearchController, isRoom, analytics])

    const isValidSearch = useMemo(() => {
        // Return true if search should proceed, false if it should reset/return early
        return !((searchTerm.length < 2) &&
            !messageSearchController.isRetracted &&
            !messageSearchController.onlyMe &&
            !messageSearchController.endDate)
    }, [searchTerm, messageSearchController,
        messageSearchController.messages,
        messageSearchController.isRetracted,
        messageSearchController.onlyMe,
        messageSearchController.endDate]
    )

    const search = useCallback(() => {
        if (!isValidSearch) return

        // const isOnlyRetractedSelected = messageSearchController.isRetracted &&
        //     !messageSearchController.onlyMe &&
        //     !messageSearchController.notMe &&
        //     !messageSearchController.isUrgent

        if (searchTerm !== messageSearchController.searchTerm) {
            messageSearchController.setSearchTerm(searchTerm)
        }

        try {
            messageSearchController.debouncedSearch()
        } catch (error) {
            addToast({
                color: 'danger',
                message: error.message
            })
        }
    }, [isValidSearch, searchTerm])

    useEffect(() => {
        search()
    }, [searchTerm,
        messageSearchController.isRetracted,
        messageSearchController.isUrgent,
        messageSearchController.onlyMe,
        messageSearchController.notMe,
        search])

    useEffect(() => {
        if (!isValidSearch) {
            messageSearchController.resetSearchMeta()
        }
    }, [isValidSearch, messageSearchController, messageSearchController.messages.length])

    /**
     * There's a chance the owners of the messages won't exist in the store so must be retrieved
     */
    const retrieveMessageUsers = useCallback(() => {
        const ownerIds = messageSearchController.messages.map(m => m.ownerId)

        if (modelType === 'room') {
            fetchUserService.fetchSpecificUsersFromRoom(modelId, ownerIds)
        } else {
            fetchUserService.fetchSpecificUsersFromThread(modelId, ownerIds)
        }
    }, [messageSearchController, modelId, modelType, fetchUserService])

    useEffect(() => {
        if (messageSearchController.messages.length)
            retrieveMessageUsers()
    }, [messageSearchController.messages, retrieveMessageUsers])

    const renderSearchResult = (msg) => {
        if (msg.type === 'event') return null

        const basePath = modelType === 'room'
            ? (msg.repliesToId
                ? `/room/${modelId}/replies/${msg.repliesToId}`
                : `/room/${modelId}`)
            : `/thread/${modelId}`
        const to = `${basePath}?msgId=${msg.id}&jump`

        // Use replace when staying in the same reply context (both null or both same number)
        // Use push (replace=false) when navigating to a different reply context
        const msgRepliesToId = msg.repliesToId ?? null
        const shouldReplace = msgRepliesToId === currentRepliesToId

        const cardContent = (
            <>
                <Text
                    italic
                    size="smaller"
                    className="ps-2 pt-2"
                >
                    {msg.repliesToId ? (
                        <><Text bold>Replied</Text> to a message in <Text bold>#{modelName}</Text> </>
                    ) : (
                        <>Posted {isRoom && <>to <Text bold>#{modelName}</Text> </>} </>
                    )}
                    on <Text bold>{moment(msg.publishedDate).format('MMM DD, YYYY')}</Text>
                </Text>
                <Col>
                    <Message
                        message={msg}
                        modelType={modelType}
                        showHoverBar={false}
                        forceHeader={true}
                        highlightString={messageSearchController.searchTerm}
                        allowRetracted={true}
                        variant="search"
                    />
                </Col>
            </>
        )

        return (
            <Card
                className="mb-4 pb-2 mx-3 page"
                key={msg.id}
            >
                {msg.isRetracted ? (
                    <div>{cardContent}</div>
                ) : (
                    <Link
                        replace={shouldReplace}
                        to={to}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                        onClick={() => onClose?.()}
                    >
                        {cardContent}
                    </Link>
                )}
            </Card>
        )
    }

    const handleDateRangeChange = (update: [Date | null, Date | null]) => {
        const [startDate, endDate] = update

        setDateRange([startDate, endDate])

        // Only proceed if both dates are set or both are null
        if ((startDate && endDate) || (!startDate && !endDate)) {
            const adjustedStartDate = startDate
                ? moment(startDate).startOf('day').toDate()
                : null
            const adjustedEndDate = endDate
                ? moment(endDate).endOf('day').toDate()
                : null

            messageSearchController.setStartDate(adjustedStartDate)
            messageSearchController.setEndDate(adjustedEndDate)
            search()
        }
    }

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            size="xl">
            <ModalHeader
                title={`Search for Messages in ${modelName}`}
                truncateTitle
            />
            <ModalBody>
                <Row className="flex-wrap">
                    <Col md={7} className="order-2 order-lg-1 rounded p-2 position-relative message-search-results">
                        <div className="mx-3 pb-2">
                            {(!!messageSearchController.messagesCount) &&
                                <Text
                                    italic
                                    className="mb-2">
                                    {messageSearchController.messagesCount} {messageSearchController.messagesCount !== 1 ? 'results' : 'result'}
                                </Text>
                            }
                            {(messageSearchController.isLoading) &&
                                <div className="text-center position-relative"
                                    aria-live="polite"
                                >
                                    <div className="position-absolute mt-5 start-50 translate-middle"
                                        style={{top: 10}}>
                                        <Spinner width={100} />
                                    </div>
                                    <span role="alert" aria-live="polite">Searching {modelType}...</span>
                                </div>
                            }
                            {(isValidSearch && !messageSearchController.messages?.length && !messageSearchController.isLoading) &&
                                <div className="text-center">
                                    <span role="alert" aria-live="polite" className="text-nowrap">No results. Try modifying your search.</span>
                                </div>
                            }
                            {(!isValidSearch && !messageSearchController.isLoading) &&
                                <div className="text-center">
                                    <span role="alert" aria-live="polite" className="text-nowrap">Please select search options.</span>
                                </div>
                            }
                        </div>
                        <PerfectScrollbar
                            options={{ suppressScrollX: true }}
                            style={{
                                height: '73vh'
                            }}>

                            {messageSearchController.messages?.map(renderSearchResult)}

                        </PerfectScrollbar>
                    </Col>
                    <Col className="order-1 order-lg-2 p-3">
                        <div className="mb-3">
                            <label className="fw-bold w-100">Search for keywords
                                <Input
                                    ref={searchInputRef}
                                    type="text"
                                    name="searchText"
                                    value={searchTerm}
                                    className="p-3"
                                    autoFocus
                                    style={{
                                        border: '2px solid #8f9ba6'
                                    }}
                                    placeholder="Search"
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </label>
                        </div>
                        <Col className="mb-3">
                            <Text
                                tag="h4"
                                className="pb-2"
                            >
                                Filter by
                            </Text>
                            <fieldset className="border-0 p-0">
                                <legend className="border-bottom mb-3 w-100">
                                    <strong>People</strong>
                                </legend>
                                <Checkbox
                                    label="Only search messages from me"
                                    id="onlyMe"
                                    name="onlyMe"
                                    checked={messageSearchController.onlyMe}
                                    onChange={(e) => { messageSearchController.setOnlyMe(e.checked) }}
                                />
                                <Checkbox
                                    id="notMe"
                                    name="notMe"
                                    label="Don't search messages from me"
                                    checked={messageSearchController.notMe}
                                    onChange={(e) => { messageSearchController.setNotMe(e.checked) }}
                                />
                            </fieldset>
                        </Col>
                        <Col className="mb-3">
                            <fieldset className="border-0 p-0">
                                <legend className="border-bottom mb-3 w-100">
                                    <strong>Attributes</strong>
                                </legend>
                                <Checkbox
                                    id="urgent"
                                    name="urgent"
                                    label="Urgent Messages Only"
                                    checked={messageSearchController.isUrgent}
                                    onChange={(e) => { messageSearchController.setIsUrgent(e.checked) }}
                                />
                                <Checkbox
                                    id="retracted"
                                    name="retracted"
                                    label="Retracted Messages Only (by me)"
                                    checked={messageSearchController.isRetracted}
                                    onChange={(e) => { messageSearchController.setIsRetracted(e.checked) }}
                                />
                            </fieldset>
                        </Col>
                        <Col className="mb-3">
                            <label>
                                <div className="border-bottom mb-3">
                                    <strong>Date Range</strong>
                                </div>
                                <ReactDatePicker
                                    selectsRange
                                    startDate={dateRange[0]}
                                    endDate={dateRange[1]}
                                    maxDate={new Date()}
                                    onChange={handleDateRangeChange}
                                    isClearable={true}
                                    className="form-control"
                                    placeholderText="Select range..."
                                    monthsShown={2}
                                    popperPlacement="bottom-end"
                                    calendarClassName="datepicker"
                                    wrapperClassName="w-100"
                                />
                            </label>
                        </Col>
                    </Col>
                </Row>
            </ModalBody>
        </Modal>
    )
})

export { MessageSearchModal }
